import { useMemo, useState } from 'react';
import {
  Building2,
  Bell,
  Link2,
  Plus,
  RefreshCw,
  Save,
  Unplug,
  Upload,
  UserPlus,
  Users,
  X,
} from 'lucide-react';

import { useToast } from '../contexts/ToastContext.jsx';
import { agents, ORG } from '../data/seedData.js';

// ── constants ────────────────────────────────────────────────────────────────

const TIMEZONE_OPTIONS = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Mexico_City',
  'America/Sao_Paulo',
  'Europe/London',
];

const ROLE_LABELS = {
  team_leader: 'Team Leader',
  agent: 'Agent',
  isa: 'ISA',
};

const ROLE_OPTIONS = [
  { value: 'team_leader', label: 'Team Leader' },
  { value: 'agent', label: 'Agent' },
  { value: 'isa', label: 'ISA' },
];

function randomLastLogin() {
  // Produce a deterministic "recent" last-login string per agent id.
  return (seed) => {
    const hours = (seed.charCodeAt(seed.length - 1) % 12) + 1;
    return `${hours}h ago`;
  };
}

// ── shared UI bits ───────────────────────────────────────────────────────────

function SectionCard({ icon: Icon, title, description, children, action }) {
  return (
    <section className="card p-6 md:px-7 md:py-6 space-y-4">
      <header className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-btn flex items-center justify-center bg-accent/10 text-accent shrink-0">
            <Icon size={18} />
          </div>
          <div>
            <h2 className="section-header mb-0">{title}</h2>
            {description && (
              <p className="text-xs text-text-secondary mt-1">{description}</p>
            )}
          </div>
        </div>
        {action}
      </header>
      <div>{children}</div>
    </section>
  );
}

function Field({ label, children, hint }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs uppercase tracking-wider text-text-secondary">
        {label}
      </span>
      {children}
      {hint && <span className="block text-xs text-text-secondary">{hint}</span>}
    </label>
  );
}

// ── page ─────────────────────────────────────────────────────────────────────

export default function Settings() {
  const { showToast } = useToast();

  // Section 1 — Organization profile
  const [orgProfile, setOrgProfile] = useState({
    name: ORG?.name || 'Smith Luxury Group',
    timezone: ORG?.timezone || 'America/New_York',
    logoName: '',
  });

  // Section 2 — CRM connection
  const [crm, setCrm] = useState({
    crmType: 'Go High Level',
    connected: true,
    lastSync: new Date(Date.now() - 2 * 3_600_000),
    syncing: false,
  });

  const handleSyncNow = async () => {
    setCrm((prev) => ({ ...prev, syncing: true }));
    let ok = true;
    try {
      const res = await fetch('/api/re/crm/sync', { method: 'POST' }).catch(() => null);
      if (!res || !res.ok) ok = false;
    } finally {
      setCrm((prev) => ({
        ...prev,
        syncing: false,
        lastSync: new Date(),
      }));
      showToast(ok ? 'CRM sync triggered.' : 'CRM sync queued locally.', {
        type: ok ? 'success' : 'info',
      });
    }
  };

  const handleDisconnect = () => {
    setCrm((prev) => ({ ...prev, connected: false, lastSync: null }));
    showToast('CRM disconnected.', { type: 'info' });
  };

  // Section 3 — Notifications
  const [prefs, setPrefs] = useState({
    smsHotLeads: true,
    dailySummary: true,
    weeklyReport: true,
  });

  const togglePref = (key) =>
    setPrefs((prev) => ({ ...prev, [key]: !prev[key] }));

  // Section 4 — Team management
  const lastLoginFor = useMemo(randomLastLogin, []);
  const [teamMembers, setTeamMembers] = useState(() =>
    agents.map((a) => ({
      id: a.id,
      name: a.name,
      email: a.email,
      role: a.role,
      lastLogin: lastLoginFor(a.id),
      status: 'active',
    })),
  );

  const [showAddMember, setShowAddMember] = useState(false);
  const [newMember, setNewMember] = useState({
    name: '',
    email: '',
    role: 'agent',
    password: '',
  });

  const resetNewMember = () =>
    setNewMember({ name: '', email: '', role: 'agent', password: '' });

  const handleAddMember = (e) => {
    e.preventDefault();
    if (!newMember.name || !newMember.email) return;
    setTeamMembers((prev) => [
      ...prev,
      {
        id: `agent-local-${prev.length + 1}`,
        name: newMember.name,
        email: newMember.email,
        role: newMember.role,
        lastLogin: '—',
        status: 'active',
      },
    ]);
    resetNewMember();
    setShowAddMember(false);
    showToast('Team member added.', { type: 'success' });
  };

  const handleSaveAll = () => {
    showToast('Settings saved.', { type: 'success' });
  };

  const formatLastSync = (d) => {
    if (!d) return '—';
    const diffMs = Date.now() - d.getTime();
    const diffH = Math.round(diffMs / 3_600_000);
    if (diffH < 1) return `${Math.max(1, Math.round(diffMs / 60_000))}m ago`;
    if (diffH < 24) return `${diffH}h ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  return (
    <div className="space-y-6 w-full">
      <header className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="page-title mb-0">Settings</h1>
          <p className="page-subtitle mb-0 mt-2">
            Organization-wide configuration — profile, integrations, notifications, and team.
          </p>
        </div>
        <button
          type="button"
          onClick={handleSaveAll}
          className="inline-flex items-center gap-1.5 rounded-pill bg-accent
                     text-white px-4 py-2 text-sm font-semibold
                     hover:bg-accent-hover transition-colors"
        >
          <Save size={14} /> Save
        </button>
      </header>

      {/* Section 1 — Organization Profile */}
      <SectionCard
        icon={Building2}
        title="Organization Profile"
        description="Organization name, timezone, and logo."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Organization name">
            <input
              type="text"
              value={orgProfile.name}
              onChange={(e) =>
                setOrgProfile((p) => ({ ...p, name: e.target.value }))
              }
              className="w-full bg-bg-card border border-border-card rounded-btn
                         text-text-primary text-sm px-3 py-2
                         focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/50
                         transition-colors"
            />
          </Field>
          <Field label="Timezone">
            <select
              value={orgProfile.timezone}
              onChange={(e) =>
                setOrgProfile((p) => ({ ...p, timezone: e.target.value }))
              }
              className="w-full bg-bg-card border border-border-card rounded-btn
                         text-text-primary text-sm px-3 py-2
                         focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/50
                         transition-colors"
            >
              {TIMEZONE_OPTIONS.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="mt-4">
          <p className="text-xs uppercase tracking-wider text-text-secondary mb-2">
            Logo
          </p>
          <label
            className="flex flex-col items-center justify-center gap-2 h-32
                       rounded-btn border-2 border-dashed border-border-card
                       bg-bg-subtle text-text-secondary
                       hover:border-accent hover:text-accent cursor-pointer transition-colors"
          >
            <Upload size={22} />
            <span className="text-sm">
              {orgProfile.logoName || 'Click to upload a logo (PNG or SVG)'}
            </span>
            <input
              type="file"
              accept="image/png,image/svg+xml,image/jpeg"
              className="hidden"
              onChange={(e) =>
                setOrgProfile((p) => ({
                  ...p,
                  logoName: e.target.files?.[0]?.name || '',
                }))
              }
            />
          </label>
        </div>
      </SectionCard>

      {/* Section 2 — CRM Connection */}
      <SectionCard
        icon={Link2}
        title="CRM Connection"
        description="Sync contacts and push enrichment back to your CRM."
      >
        <div className="rounded-btn border border-border-card p-4 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-text-primary">
                {crm.crmType}
              </p>
              <p className="text-xs text-text-secondary mt-0.5">
                Last sync: {formatLastSync(crm.lastSync)}
              </p>
            </div>
            <span
              className={
                crm.connected
                  ? 'badge bg-[#DCFCE7] text-[#16A34A]'
                  : 'badge bg-[#FEE2E2] text-[#DC2626]'
              }
            >
              {crm.connected ? 'Connected' : 'Disconnected'}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleSyncNow}
              disabled={!crm.connected || crm.syncing}
              style={{ borderColor: '#EA580C', color: '#EA580C', borderWidth: '1.5px' }}
              className="inline-flex items-center gap-1.5 rounded-[20px] bg-transparent border
                         hover:bg-[rgba(234,88,12,0.10)]
                         px-4 py-1.5 text-[13px] font-medium transition-colors
                         disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <RefreshCw
                size={13}
                className={crm.syncing ? 'animate-spin' : ''}
              />
              {crm.syncing ? 'Syncing…' : 'Sync Now'}
            </button>
            <button
              type="button"
              onClick={handleDisconnect}
              disabled={!crm.connected}
              style={{ borderColor: '#9CA3AF', color: '#6B7280', borderWidth: '1.5px' }}
              className="inline-flex items-center gap-1.5 rounded-[20px] bg-transparent border
                         hover:bg-[rgba(107,114,128,0.10)] hover:text-[#333333]
                         px-4 py-1.5 text-[13px] font-medium transition-colors
                         disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Unplug size={13} /> Disconnect
            </button>
          </div>
        </div>
      </SectionCard>

      {/* Section 3 — Notification Preferences */}
      <SectionCard
        icon={Bell}
        title="Notification Preferences"
        description="When should we ping you?"
      >
        <div className="space-y-3">
          {[
            {
              key: 'smsHotLeads',
              title: 'SMS alerts on hot leads',
              hint: 'Text the assigned agent when a lead scores 80+ or is marked verified.',
            },
            {
              key: 'dailySummary',
              title: 'Daily summary email',
              hint: 'Morning digest of pipeline, attention items, and today’s showings.',
            },
            {
              key: 'weeklyReport',
              title: 'Weekly report email',
              hint: 'Monday-morning performance report to the team leader.',
            },
          ].map(({ key, title, hint }) => (
            <label
              key={key}
              className="flex items-start gap-3 rounded-btn border border-border-card
                         p-3 hover:border-accent/60 transition-colors cursor-pointer"
            >
              <input
                type="checkbox"
                checked={prefs[key]}
                onChange={() => togglePref(key)}
                className="mt-1 h-4 w-4 accent-accent"
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-text-primary">
                  {title}
                </span>
                <span className="block text-xs text-text-secondary">{hint}</span>
              </span>
            </label>
          ))}
        </div>
      </SectionCard>

      {/* Section 4 — Team Management */}
      <SectionCard
        icon={Users}
        title="Team Management"
        description="Leaders, agents, and ISAs."
        action={
          <button
            type="button"
            onClick={() => setShowAddMember((v) => !v)}
            style={{ borderColor: '#EA580C', color: '#EA580C', borderWidth: '1.5px' }}
            className="inline-flex items-center gap-1.5 rounded-[20px] bg-transparent border
                       hover:bg-[rgba(234,88,12,0.10)]
                       px-3 py-1.5 text-[13px] font-medium transition-colors"
          >
            {showAddMember ? <X size={13} /> : <UserPlus size={13} />}
            {showAddMember ? 'Cancel' : 'Add Team Member'}
          </button>
        }
      >
        {showAddMember && (
          <form
            onSubmit={handleAddMember}
            className="mb-4 rounded-btn border border-accent/40 bg-accent/5 p-4 space-y-3"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Full name">
                <input
                  type="text"
                  required
                  value={newMember.name}
                  onChange={(e) =>
                    setNewMember((p) => ({ ...p, name: e.target.value }))
                  }
                  className="w-full bg-white border border-border-card rounded-btn
                             text-text-primary text-sm px-3 py-2
                             focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/50"
                />
              </Field>
              <Field label="Email">
                <input
                  type="email"
                  required
                  value={newMember.email}
                  onChange={(e) =>
                    setNewMember((p) => ({ ...p, email: e.target.value }))
                  }
                  className="w-full bg-white border border-border-card rounded-btn
                             text-text-primary text-sm px-3 py-2
                             focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/50"
                />
              </Field>
              <Field label="Role">
                <select
                  value={newMember.role}
                  onChange={(e) =>
                    setNewMember((p) => ({ ...p, role: e.target.value }))
                  }
                  className="w-full bg-white border border-border-card rounded-btn
                             text-text-primary text-sm px-3 py-2
                             focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/50"
                >
                  {ROLE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Temporary password">
                <input
                  type="password"
                  required
                  minLength={8}
                  value={newMember.password}
                  onChange={(e) =>
                    setNewMember((p) => ({ ...p, password: e.target.value }))
                  }
                  className="w-full bg-white border border-border-card rounded-btn
                             text-text-primary text-sm px-3 py-2
                             focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/50"
                />
              </Field>
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                className="inline-flex items-center gap-1.5 rounded-pill bg-accent
                           text-white px-4 py-1.5 text-sm font-semibold
                           hover:bg-accent-hover transition-colors"
              >
                <Plus size={14} /> Add Member
              </button>
            </div>
          </form>
        )}

        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-bg-subtle border-b border-border-card">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-text-secondary">
                  Name
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-text-secondary">
                  Email
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-text-secondary">
                  Role
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-text-secondary">
                  Last Login
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-text-secondary">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {teamMembers.map((m) => (
                <tr
                  key={m.id}
                  className="border-t border-border-card"
                >
                  <td className="px-4 py-2.5 font-medium text-text-primary">
                    {m.name}
                  </td>
                  <td className="px-4 py-2.5 text-text-secondary">{m.email}</td>
                  <td className="px-4 py-2.5">
                    <span className="badge bg-bg-subtle border border-border-card text-text-secondary">
                      {ROLE_LABELS[m.role] || m.role}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-text-secondary whitespace-nowrap">
                    {m.lastLogin}
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className={
                        m.status === 'active'
                          ? 'badge bg-[#DCFCE7] text-[#16A34A]'
                          : 'badge bg-[#F3F4F6] text-[#6B7280]'
                      }
                    >
                      {m.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

    </div>
  );
}
